import { useContext } from "react";

import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";

import {
    BannerType,
    BannerView,
    colors,
    CommunicationPrintIcon,
    SaveAltIcon
} from "@dotnet/telematics-design-system";

import Button, {
    BUTTON_COLORS,
    BUTTON_SIZES,
    BUTTON_VARIANTS
} from "components/DesignComponents/Button";
import Checkbox from "components/DesignComponents/Checkbox";
import CopyButton from "components/DesignComponents/CopyButton";
import AlertContext, { ALERT_STATUS } from "context/alert/alertContext";
import { body1SemiBold } from "shared/styles/typography";

import { RecoveryCodesProps } from "./types";

const RecoveryCodes = ({ codes, onCheck }: RecoveryCodesProps) => {
    const { gray100, gray200, gray800, gray900 } = colors;

    const { setAlert } = useContext(AlertContext);

    const { t } = useTranslation();

    const handleDownload = () => {
        try {
            const content = codes.join("\n");

            const blob = new Blob([content], { type: "text/plain" });
            const url = URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = url;
            link.download = "recovery_codes.txt";
            link.click();

            URL.revokeObjectURL(url);
        } catch {
            setAlert({
                status: ALERT_STATUS.Critical,
                title: t("Security##recovery codes##download error message")
            });
        }
    };

    const handlePrint = () => {
        let printWindow: Window | null = null;

        try {
            const codesHtml = codes.map(code => `<li>${code}</li>`).join("");
            printWindow = window.open("", "_blank");

            if (!printWindow) throw new Error();

            const doc = printWindow.document;
            doc.title = "Print recovery codes";

            doc.body.innerHTML = `
                <div class="print-container">
                    <h2>Recovery codes</h2>
                    <ul>${codesHtml}</ul>
                </div>
            `;

            const style = doc.createElement("style");

            style.textContent = `
                body {
                    font-family: sans-serif; 
                    padding: 20px;
                    color: ${gray800};
                }

                h2 {
                    color: ${gray900};
                    margin-bottom: 4px;
                }

                ul {
                    list-style-type: none; 
                    padding: 0;
                    column-count: 2; 
                    column-gap: 40px;
                }

                li {
                    padding: 8px 0; 
                    border-bottom: 1px solid ${gray100}; 
                    font-family: monospace;
                    font-size: 16px;
                    break-inside: avoid;
                }

                @media print {
                    body { 
                        padding: 0; 
                    }
                        
                    .print-container { margin: 20px; }
                }
            `;

            doc.head.appendChild(style);
            doc.close();

            setTimeout(() => {
                printWindow?.focus();
                printWindow?.print();
                printWindow?.close();
            }, 250);
        } catch {
            setAlert({
                status: ALERT_STATUS.Critical,
                title: t("Security##recovery codes##print error message")
            });

            if (printWindow && !printWindow.closed) {
                printWindow.close();
            }
        }
    };

    return (
        <div>
            <BannerView
                bannerType={BannerType.Warning}
                title={t("Security##recovery codes##banner title")}
                description={t("Security##recovery codes##banner description")}
            />

            <div
                css={css({
                    border: `1px solid ${gray200}`,
                    borderRadius: "12px",
                    padding: "24px",
                    margin: "24px 0 16px 0"
                })}
            >
                <div
                    css={css({
                        display: "grid",
                        gridTemplateColumns: "repeat(2, 1fr)",
                        columnGap: "8px",
                        rowGap: "16px",
                        marginBottom: "24px"
                    })}
                >
                    {codes.map(code => (
                        <span
                            key={code}
                            css={[body1SemiBold, { whiteSpace: "nowrap" }]}
                        >
                            {code}
                        </span>
                    ))}
                </div>

                <div
                    css={css({
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "8px"
                    })}
                >
                    <CopyButton
                        size={BUTTON_SIZES.Tiny}
                        copiedValue={codes.join(", ")}
                        title={t("Button##copy all")}
                    />

                    <Button
                        color={BUTTON_COLORS.Secondary}
                        size={BUTTON_SIZES.Tiny}
                        variant={BUTTON_VARIANTS.IconLeft}
                        icon={<SaveAltIcon />}
                        onClick={handleDownload}
                    >
                        {t("Button##download")}
                    </Button>

                    <Button
                        color={BUTTON_COLORS.Secondary}
                        size={BUTTON_SIZES.Tiny}
                        variant={BUTTON_VARIANTS.IconLeft}
                        icon={<CommunicationPrintIcon />}
                        onClick={handlePrint}
                    >
                        {t("Button##print")}
                    </Button>
                </div>
            </div>

            <Checkbox
                label={t("Security##recovery codes##checkbox title")}
                onChange={(_, isChecked) => onCheck(isChecked)}
            />
        </div>
    );
};

export { RecoveryCodes };
